import React from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'react-toastify';
import { SmartToy } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { FormContainer } from 'react-hook-form-mui';
import dayjs from 'dayjs';

import { Button } from 'components/forms/Buttons';
import ButtonDialog from 'components/forms/ButtonDialog';
import { TextInput, DateTimePickerInput } from 'components/forms/Inputs';
import { useDialogContext } from 'components/dialog/DialogContext';
import { useListContext } from 'components/list/ListContext';
import QUERIES from 'services/gqlQueries';
import { SubEventType } from 'types/App';

const FORM_ID = 'create_virtual_competitor';

type FormValues = {
  name: string;
  startTime: dayjs.Dayjs;
};

type BodyProps = {
  subEvent: SubEventType;
  defaultName: string;
};

const CreateVirtualCompetitorBody = ({ subEvent, defaultName }: BodyProps) => {
  const { refetch } = useListContext();
  const { close } = useDialogContext();

  const [createCompetitor] = useMutation<{item: {name?: string}}>(QUERIES.createCompetitor);

  const handleSubmit = async (values: FormValues) => {
    try {
      const resp = await createCompetitor({
        variables: {
          subEventId: subEvent.id,
          payload: {
            isVirtual: true,
            name: values.name?.trim() || undefined,
            startTime: values.startTime.toDate()
          }
        }
      });
      const competitor = resp.data?.item;
      toast.success(`Virtual competitor "${competitor?.name}" created`);
      if (typeof refetch === 'function') refetch();
      close();
    } catch (e) {
      if (e instanceof Error && e.message) toast.error(e.message);
    }
  };

  return (
    <FormContainer<FormValues>
      FormProps={{ id: FORM_ID, noValidate: true }}
      defaultValues={{
        name: defaultName,
        startTime: dayjs(subEvent.event.startDate)
      }}
      onSuccess={handleSubmit}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Creates a competitor without a user, assigned to a dedicated virtual device with a predefined GPS track.
        </Typography>
        <TextInput fullWidth label="Name" name="name" autoFocus />
        <DateTimePickerInput fullWidth format="YYYY-MM-DD HH:mm" name="startTime" label="Start Time" />
      </Stack>
    </FormContainer>
  );
};

type Props = {
  subEvent: SubEventType;
};

const CreateVirtualCompetitorButton = ({ subEvent }: Props) => {
  const defaultName = `Virtual`;

  return (
    <ButtonDialog
      buttonLabel="Add Virtual Competitor"
      startIcon={<SmartToy />}
      dialogTitle="Add Virtual Competitor"
      maxWidth="sm"
      primaryButton={
        <Button type="submit" form={FORM_ID}>
          Create
        </Button>
      }
    >
      <CreateVirtualCompetitorBody subEvent={subEvent} defaultName={defaultName} />
    </ButtonDialog>
  );
};

export default CreateVirtualCompetitorButton;
